import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  resolveBodyFontFamily,
  resolveFormInputTheme,
} from "../theme/themeValueResolver";
import { useWBTheme, useEventTimezone } from "../theme/WBThemeRuntimeProvider";
import { useWBLang } from "../lang/WBLanguageRuntimeProvider";
import { resolveText, type LangText } from "../lib/resolve-text";
import { DataType, type Field } from "../types/core";
import {
  extractWallParts,
  wallPartsToIso,
  type WallParts,
} from "../../lib/utils/datetime-tz";
import { t, getLocale } from "../locale";
import {
  parseLocaleNumber,
  toEditableLocaleString,
} from "../lib/locale-number";
import { sortOptionsForDisplay } from "../../lib/utils/sortOptionsForDisplay";
import { normalizeAcceptancePrefill } from "./acceptanceField";
import {
  IMAGE_CLEAR_SENTINEL,
  IMAGE_FILE_ACCEPT,
  MAX_PUBLIC_AF_IMAGE_BYTES,
  isStoredImageValue,
  uploadAfImage,
  validateImageFile,
} from "../lib/image-upload";
import {
  type BorderSide,
  type CollectedFieldValues,
  WB_FIELD_CHANGED_EVENT,
  WB_VALIDATE_EVENT,
  classNames,
  extractUtilityClasses,
  getBorderClasses,
  toCssLength,
  useCompanionIndexOverride,
  useDataInputDraftSnapshot,
  useDataInputReadOnly,
  useLiveGuestValues,
  isPlainRecord,
  resolveRecordValueByField,
} from "./shared";
import { evaluateConditionGroup } from "../lib/evaluate-condition";
import { normalizeConditionData } from "../../lib/normalizeFieldValue";
import { normalizedConditionTree } from "../../lib/conditionOperands";

/* ------------------------------------------------------------------ */
/*  Validation constants                                               */
/* ------------------------------------------------------------------ */

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const VALID_LONG_PATTERN = /^-?\d+$/;
const VALID_DOUBLE_PATTERN = /^-?\d+(\.\d+)?$/;

/* ------------------------------------------------------------------ */
/*  Timezone helpers — convert between wall-clock strings (what the    */
/*  HTML5 date/datetime-local inputs use) and UTC instants (what the   */
/*  backend stores). All comparisons happen in the instant domain so   */
/*  bounds checks stay correct across DST.                              */
/* ------------------------------------------------------------------ */

/** Parse a "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm[:ss]" string into wall parts.
 *  Time may have an optional `:ss` suffix (Safari occasionally emits one);
 *  we read only the first two `:`-separated components so seconds are
 *  silently dropped without touching the original string. */
function parseWallString(value: string): WallParts | null {
  const [datePart, timePart] = value.split("T");
  if (!datePart) return null;
  const [yStr, mStr, dStr] = datePart.split("-");
  if (!yStr || !mStr || !dStr) return null;
  const year = parseInt(yStr, 10);
  const month = parseInt(mStr, 10) - 1;
  const day = parseInt(dStr, 10);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  let hour = 0;
  let minute = 0;
  if (timePart) {
    const [hStr, minStr] = timePart.split(":");
    hour = parseInt(hStr ?? "0", 10);
    minute = parseInt(minStr ?? "0", 10);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
      hour = 0;
      minute = 0;
    }
  }
  return { year, month, day, hour, minute };
}

/**
 * Convert a wall-clock string in `tz` to a UTC instant (ms). When the string
 * is a UTC ISO (ends with `Z`) or already a number, parse directly.
 */
function wallStringToInstant(value: string, tz: string): number | null {
  if (!value) return null;
  if (value.endsWith("Z")) {
    const t = Date.parse(value);
    return Number.isFinite(t) ? t : null;
  }
  const parts = parseWallString(value);
  if (!parts) return null;
  const iso = wallPartsToIso(parts, tz);
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

/**
 * If `value` is a UTC ISO (ends with Z), convert to a wall-clock string in
 * `tz` formatted for the native HTML5 input (`YYYY-MM-DDTHH:mm` for
 * DATE_TIME, `YYYY-MM-DD` for DATE). Non-ISO inputs (already wall-clock,
 * numeric, or empty) pass through unchanged.
 */
function toWallInputValue(
  value: string,
  type: DataType,
  tz: string,
): string {
  if (!value || !value.endsWith("Z")) return value;
  const parts = extractWallParts(value, tz);
  const datePart = `${parts.year}-${String(parts.month + 1).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
  if (type === DataType.DATE) return datePart;
  if (type === DataType.DATE_TIME) {
    const timePart = `${String(parts.hour).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}`;
    return `${datePart}T${timePart}`;
  }
  return value;
}

/* ------------------------------------------------------------------ */
/*  Validation logic                                                   */
/* ------------------------------------------------------------------ */

/** Separator readInputValue uses to join a MULTI_SELECT's picked option values
 *  for the count validator. ASCII Unit Separator — never present in a real
 *  option label, so a comma inside a label can't inflate the pick count. */
const MULTI_SELECT_COUNT_SEP = "\x1f";

function validateFieldValue(
  rawValue: string,
  type: DataType,
  required: boolean,
  field: Field | undefined,
  eventTz: string,
  overrideMin?: string,
  overrideMax?: string,
  /** M23: the guest typed something a numeric field could not parse. */
  unparsableNumber = false,
): string {
  const value = rawValue.trim();

  // M23: BEFORE the required check, deliberately. "abc" in a number field is
  // not a missing value and "this field is required" would be a confusing
  // thing to say about text the guest can plainly see they typed.
  if (unparsableNumber) return t("invalidNumber");

  // Per-field min/max overrides (set by the builder) take precedence over the
  // schema's field.lowerBound/upperBound; we fall back to the schema when no
  // override is present. For STRING/EMAIL these are character-length bounds;
  // for LONG/DOUBLE they are numeric value bounds; for DATE/DATE_TIME they are
  // wall-clock date strings (already tz-normalized by the caller).
  const numericOverride = (s: string | undefined): number | undefined => {
    if (s == null || s.trim() === "") return undefined;
    const n = Number(s);
    return Number.isFinite(n) ? n : undefined;
  };
  const effLower =
    numericOverride(overrideMin) ??
    (typeof field?.lowerBound === "number" ? field.lowerBound : undefined);
  const effUpper =
    numericOverride(overrideMax) ??
    (typeof field?.upperBound === "number" ? field.upperBound : undefined);

  // Required check
  if (required && !value) {
    switch (type) {
      case DataType.EMAIL:
        return t("emailRequired");
      case DataType.DATE:
      case DataType.DATE_TIME:
        return t("dateRequired");
      case DataType.SINGLE_SELECT:
      case DataType.MULTI_SELECT:
        return t("selectRequired");
      default:
        return t("fieldRequired");
    }
  }

  if (!value) return "";

  // Type-specific validation
  switch (type) {
    case DataType.EMAIL: {
      if (!EMAIL_REGEX.test(value)) return t("invalidEmail");
      if (typeof effLower === "number" && value.length < effLower)
        return t("minChars", String(effLower));
      if (typeof effUpper === "number" && value.length > effUpper)
        return t("maxChars", String(effUpper));
      break;
    }

    case DataType.URL: {
      // A guest enters a bare http(s) URL; the canonical [label](url) form is
      // also accepted for parity with admin-entered values. Regexes + the
      // 2000-char cap mirror main-frontend src/lib/utils/urlField.ts.
      if (value.length > 2000) return t("maxChars", "2000");
      // Host must have a dot + 2+ letter TLD (rejects `https://sadf`).
      const core =
        "https?:\\/\\/(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+[a-zA-Z]{2,}(?::\\d+)?(?:[/?#][^\\s)]*)?";
      const bareUrl = new RegExp(`^${core}$`, "i");
      const mdUrl = new RegExp(`^\\[[^\\]]*\\]\\((?:${core})\\)$`, "i");
      if (!bareUrl.test(value) && !mdUrl.test(value)) return t("invalidUrl");
      break;
    }

    case DataType.STRING: {
      if (typeof effLower === "number" && value.length < effLower)
        return t("minChars", String(effLower));
      if (typeof effUpper === "number" && value.length > effUpper)
        return t("maxChars", String(effUpper));
      break;
    }

    case DataType.LONG: {
      if (!VALID_LONG_PATTERN.test(value))
        return t("invalidInteger");
      const numValue = Number(value);
      if (typeof effLower === "number" && numValue < effLower)
        return t("minValue", String(effLower));
      if (typeof effUpper === "number" && numValue > effUpper)
        return t("maxValue", String(effUpper));
      break;
    }

    case DataType.CURRENCY:
    case DataType.DOUBLE: {
      // `value` is already canonical (readInputValue returns data-canonical for
      // locale-aware numeric inputs), so the dot-decimal pattern + Number()
      // bounds check operate on the normalized value.
      if (!VALID_DOUBLE_PATTERN.test(value))
        return t("invalidNumber");
      const numValue = Number(value);
      if (typeof effLower === "number" && numValue < effLower)
        return t("minValue", String(effLower));
      if (typeof effUpper === "number" && numValue > effUpper)
        return t("maxValue", String(effUpper));
      break;
    }

    case DataType.MULTI_SELECT: {
      // `value` is the picked option values joined by MULTI_SELECT_COUNT_SEP
      // (see readInputValue — NOT a comma, so labels containing commas count
      // correctly). Bounds here are a SELECTION COUNT (min/max # of options a
      // guest may pick). Parse them as integers — the schema stores them as
      // strings, so the `typeof === "number"` effLower/effUpper guards above
      // no-op for this type; read the raw bound / override directly instead.
      const count = value
        ? value.split(MULTI_SELECT_COUNT_SEP).filter(Boolean).length
        : 0;
      const parseCount = (
        override: string | undefined,
        bound: unknown,
      ): number | undefined => {
        const src =
          override != null && override.trim() !== ""
            ? override
            : typeof bound === "number"
              ? String(bound)
              : typeof bound === "string" && bound.trim() !== ""
                ? bound
                : undefined;
        if (src === undefined) return undefined;
        const n = parseInt(src, 10);
        return Number.isFinite(n) ? n : undefined;
      };
      const minSel = parseCount(overrideMin, field?.lowerBound);
      const maxSel = parseCount(overrideMax, field?.upperBound);
      if (minSel !== undefined && count < minSel)
        return t("minOptions", String(minSel));
      if (maxSel !== undefined && count > maxSel)
        return t("maxOptions", String(maxSel));
      break;
    }

    case DataType.DATE:
    case DataType.DATE_TIME: {
      // Compare in the UTC instant domain. `value` is a wall-clock string in
      // eventTz (from the native HTML5 input); bounds may be either a UTC ISO
      // (backend supplies `…Z`) or — after pre-population — a wall-clock
      // string in eventTz. `wallStringToInstant` handles both shapes.
      const valueInstant = wallStringToInstant(value, eventTz);
      // Format an instant as "YYYY-MM-DD" in the event tz so the error
      // message matches what the input shows the user.
      const wallDateStr = (instant: number): string => {
        const parts = extractWallParts(new Date(instant).toISOString(), eventTz);
        return `${parts.year}-${String(parts.month + 1).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
      };
      const lowerSrc =
        overrideMin && overrideMin.trim() !== ""
          ? overrideMin
          : field?.lowerBound != null
            ? field.lowerBound instanceof Date
              ? field.lowerBound.toISOString()
              : String(field.lowerBound)
            : null;
      const upperSrc =
        overrideMax && overrideMax.trim() !== ""
          ? overrideMax
          : field?.upperBound != null
            ? field.upperBound instanceof Date
              ? field.upperBound.toISOString()
              : String(field.upperBound)
            : null;
      if (valueInstant !== null && lowerSrc != null) {
        const minInstant = wallStringToInstant(lowerSrc, eventTz);
        if (minInstant !== null && valueInstant < minInstant) {
          return t("dateAfter", wallDateStr(minInstant));
        }
      }
      if (valueInstant !== null && upperSrc != null) {
        const maxInstant = wallStringToInstant(upperSrc, eventTz);
        if (maxInstant !== null && valueInstant > maxInstant) {
          return t("dateBefore", wallDateStr(maxInstant));
        }
      }
      break;
    }
  }

  return "";
}

function readInputValue(
  container: HTMLElement,
  type: DataType,
): string {
  if (type === DataType.IMAGE) {
    // Hidden `data-image-pending` input semantics:
    //   pendingId          → fresh staged upload (present).
    //   IMAGE_CLEAR_SENTINEL → explicit remove of the stored image → ""
    //                          so a required field fails validation again.
    //   ""                 → untouched; a STORED image (data-image-stored)
    //                          still satisfies `required`, so surface a
    //                          non-empty marker for the validator/live map.
    const el = container.querySelector<HTMLInputElement>(
      "input[data-image-pending]",
    );
    if (el == null) return "";
    if (el.value === IMAGE_CLEAR_SENTINEL) return "";
    if (el.value) return el.value;
    return el.dataset.imageStored === "true" ? "__stored__" : "";
  }
  if (type === DataType.BOOLEAN) {
    const select = container.querySelector("select") as HTMLSelectElement | null;
    return select?.value ?? "";
  }
  if (type === DataType.MULTI_SELECT) {
    const select = container.querySelector("select") as HTMLSelectElement | null;
    if (!select) return "";
    // Join with the ASCII Unit Separator, NOT a comma: option VALUES are
    // arbitrary admin strings that may contain commas, and the MULTI_SELECT
    // count validator splits this back apart to count picks. A comma join let
    // a single "Cheese, extra" pick count as 2 and disagree with the backend's
    // array-length count. \x1f never occurs in a human option label. (This
    // joined string feeds validation only — submission reads the structural
    // array via collectFieldValues.)
    return Array.from(select.selectedOptions)
      .map((opt) => opt.value)
      .filter(Boolean)
      .join(MULTI_SELECT_COUNT_SEP);
  }
  const el = container.querySelector(
    "input, textarea, select",
  ) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
  if (el == null) return "";
  // Locale-aware numeric inputs (DOUBLE / CURRENCY) carry the CANONICAL
  // dot-decimal value in `data-canonical` (computed on change from the locale
  // draft). Read that so validation + submission see "3519.5", never the
  // displayed "3.519,5".
  const canonical = (el as HTMLElement).dataset?.canonical;
  if (canonical != null) return canonical;
  return el.value ?? "";
}

/**
 * M23: did the guest type something into a locale-aware numeric field that
 * could not be parsed? Distinct from "empty", which the canonical alone
 * cannot express.
 */
function hasUnparsableNumber(container: HTMLElement): boolean {
  return (
    container.querySelector<HTMLInputElement>('input[data-number-invalid="true"]') !=
    null
  );
}

function notifyFieldChanged(
  fieldVariable: string,
  value: string,
  // P9-F2: companion-scoped values must not pollute the MAIN form's live
  // map — App routes them into the modal-scoped overlay instead.
  companion = false,
  companionIndex = "",
) {
  window.dispatchEvent(
    new CustomEvent(WB_FIELD_CHANGED_EVENT, {
      detail: { field: fieldVariable, value, companion, companionIndex },
    }),
  );
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Standard fields not included in the runtime's additional-fields list. */
const STANDARD_FIELD_TYPES: Record<string, DataType> = {
  emailOptIn: DataType.BOOLEAN,
};

function fieldTypeFromProps(
  overwriteType: DataType | undefined,
  field: Field | undefined,
  inputFieldVariable?: string,
): DataType {
  if (overwriteType) return overwriteType;
  if (field?.dataType) return field.dataType;
  if (inputFieldVariable && inputFieldVariable in STANDARD_FIELD_TYPES)
    return STANDARD_FIELD_TYPES[inputFieldVariable]!;
  return DataType.STRING;
}

function buildInputCommonStyle(
  background: string,
  color: string,
  fontFamily: string,
  fontSize?: string,
): CSSProperties {
  return {
    backgroundColor: background,
    color,
    fontFamily,
    ...(fontSize ? { fontSize } : {}),
  };
}

function resolveSessionPrefillValue(
  session: unknown,
  inputFieldVariable: string,
): unknown {
  if (!isPlainRecord(session)) return undefined;

  const sessionRecord = session;
  const userRecord = isPlainRecord(sessionRecord.user)
    ? sessionRecord.user
    : undefined;
  const userAdditionalFields =
    userRecord && isPlainRecord(userRecord.additionalFields)
      ? userRecord.additionalFields
      : undefined;
  const sessionAdditionalFields = isPlainRecord(sessionRecord.additionalFields)
    ? sessionRecord.additionalFields
    : undefined;

  const valueFromUser = userRecord
    ? resolveRecordValueByField(userRecord, inputFieldVariable)
    : undefined;
  if (valueFromUser !== undefined) return valueFromUser;

  const valueFromSession = resolveRecordValueByField(
    sessionRecord,
    inputFieldVariable,
  );
  if (valueFromSession !== undefined) return valueFromSession;

  if (userAdditionalFields) {
    const valueFromUserAdditional = resolveRecordValueByField(
      userAdditionalFields,
      inputFieldVariable,
    );
    if (valueFromUserAdditional !== undefined) return valueFromUserAdditional;
  }

  if (sessionAdditionalFields) {
    const valueFromSessionAdditional = resolveRecordValueByField(
      sessionAdditionalFields,
      inputFieldVariable,
    );
    if (valueFromSessionAdditional !== undefined)
      return valueFromSessionAdditional;
  }

  return undefined;
}

function resolveCompanionSessionPrefillValue(
  session: unknown,
  inputFieldVariable: string,
  companionIndex: number,
): unknown {
  if (!isPlainRecord(session)) return undefined;

  const sessionRecord = session;
  const userRecord = isPlainRecord(sessionRecord.user)
    ? sessionRecord.user
    : undefined;
  const companionsValue = userRecord?.companions ?? sessionRecord.companions;

  if (!Array.isArray(companionsValue)) return undefined;
  const companionRecordRaw = companionsValue[companionIndex];
  if (!isPlainRecord(companionRecordRaw)) return undefined;
  const companionRecord = companionRecordRaw as Record<string, unknown>;

  const valueFromCompanion = resolveRecordValueByField(
    companionRecord,
    inputFieldVariable,
  );
  if (valueFromCompanion !== undefined) return valueFromCompanion;

  const companionAdditionalFields = isPlainRecord(
    companionRecord.additionalFields,
  )
    ? companionRecord.additionalFields
    : undefined;
  if (!companionAdditionalFields) return undefined;

  return resolveRecordValueByField(
    companionAdditionalFields,
    inputFieldVariable,
  );
}

function resolveDraftPrefillValue(
  draftSnapshot: CollectedFieldValues | null,
  inputFieldVariable: string,
  isCompanion: boolean,
  companionIndex: number,
): unknown {
  if (!draftSnapshot) return undefined;

  if (!isCompanion) {
    return resolveRecordValueByField(draftSnapshot.values, inputFieldVariable);
  }

  const companion = draftSnapshot.companions[companionIndex];
  if (!isPlainRecord(companion)) return undefined;
  return resolveRecordValueByField(companion, inputFieldVariable);
}

function normalizeInputDefaultValue(
  value: unknown,
  type: DataType,
): string | string[] | undefined {
  if (value === undefined || value === null) return undefined;

  // IMAGE fields have no default/prefill: a session-prefilled stored value
  // is an object ({key, contentType, size}) that would stringify to
  // "[object Object]", and the widget starts empty by design (leaving it
  // empty keeps the stored image server-side).
  if (type === DataType.IMAGE) return undefined;

  if (type === DataType.MULTI_SELECT) {
    if (Array.isArray(value)) {
      return value.map((item) => String(item));
    }
    const asString = String(value).trim();
    if (!asString) return [];
    if (asString.includes(",")) {
      return asString
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [asString];
  }

  if (typeof value === "boolean") return value ? "true" : "false";
  if (Array.isArray(value)) return value.map((item) => String(item)).join(", ");
  return String(value);
}

function parseBooleanValue(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
    return undefined;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return undefined;
    if (["true", "1", "yes", "y", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "n", "off"].includes(normalized)) return false;
  }
  return undefined;
}

function resolveDataInputSubmittedFlag(session: unknown): boolean {
  if (!isPlainRecord(session)) return false;

  const sessionRecord = session;
  const userRecord = isPlainRecord(sessionRecord.user)
    ? sessionRecord.user
    : undefined;
  const sessionAdditionalFields = isPlainRecord(sessionRecord.additionalFields)
    ? sessionRecord.additionalFields
    : undefined;
  const userAdditionalFields =
    userRecord && isPlainRecord(userRecord.additionalFields)
      ? userRecord.additionalFields
      : undefined;

  const valueSources: Array<Record<string, unknown> | undefined> = [
    userRecord,
    sessionRecord,
    userAdditionalFields,
    sessionAdditionalFields,
  ];
  const keys = [
    "dataInputSubmitted",
    "hasSubmittedDataInput",
    "websiteDataSubmitted",
    "dataSubmitted",
    "dataInputCompleted",
  ];

  // `submittedAt` — the server's own answer, and since 2026-08-14 the only
  // one that is actually sent. The five boolean keys below were never emitted
  // by the Worker: `guestToDto` shipped 23 fields and none of them was on
  // this list, so this whole function returned `false` for every guest on
  // every event, and the localStorage marker was the sole source of truth —
  // a marker `clearSession()` deliberately deletes on logout. That is why
  // logging out and back in handed the guest an empty form again.
  //
  // Read FIRST and separately from the boolean keys, because it is a
  // timestamp: `parseBooleanValue` accepts true/1/yes/on and rejects an ISO
  // string, so routing it through that helper would silently discard it.
  // Presence is the signal; the value is informational.
  for (const source of valueSources) {
    if (!source) continue;
    const submittedAt = resolveRecordValueByField(source, "submittedAt");
    if (typeof submittedAt === "string" && submittedAt.trim().length > 0) return true;
    if (submittedAt instanceof Date) return true;
  }

  for (const source of valueSources) {
    if (!source) continue;
    for (const key of keys) {
      const parsed = parseBooleanValue(resolveRecordValueByField(source, key));
      if (parsed !== undefined) return parsed;
    }
  }

  return false;
}

/* ------------------------------------------------------------------ */
/*  useFieldValidation hook                                            */
/* ------------------------------------------------------------------ */

/**
 * Where a field registers its validator. Normally the enclosing `<form>`;
 * PORTALED fields (the companion editor modal renders on document.body) have
 * no form ancestor at all — they used to register nothing, which is why the
 * modal could save empty required fields in silence. Such containers opt in
 * with `data-wb-validation-host`.
 */
export const resolveValidationHost = (el: Element | null | undefined) =>
  el?.closest("form, [data-wb-validation-host]") ?? null;

/**
 * Whether a validation pass covers this field. A field hidden by
 * `display:none` (an inactive step section) normally opts itself out — it is
 * not the visitor's current business. `includeHidden` overrides that, and
 * SUBMIT sets it: the step pills let a visitor jump past a step, so its
 * required fields would otherwise be checked by nobody. The modal host sets
 * it too, since a position:fixed card reports `offsetParent === null`.
 */
export const shouldValidateField = (
  container: HTMLElement | null,
  includeHidden: boolean,
): boolean => includeHidden || !container || container.offsetParent !== null;

/** The detail object a WB_VALIDATE_EVENT carries. */
export type ValidationDetail = {
  errorCount: number;
  includeHidden?: boolean;
  hiddenErrorNodes?: Element[];
};

/**
 * Record ONE validation error, and report the node when it is off-screen.
 *
 * `decideValidationOutcome` compares `errorCount` against how many of those
 * errors were reported as hidden: an unreported hidden error dead-ends the
 * submit button with its message on a `display:none` step and nothing on
 * screen to explain it. So the two must move together — hence one helper
 * rather than the block copy-pasted into every validating component, which is
 * how `bookingGroup` and `sessionGroup` came to bump the count without ever
 * reporting a node.
 *
 * Call once PER ERROR: a group that raises two in a single pass must report
 * twice, or the gate's arithmetic goes out of step.
 */
export function reportValidationError(
  detail: ValidationDetail | undefined,
  container: HTMLElement | null,
): void {
  if (!detail) return;
  detail.errorCount++;
  if (
    detail.includeHidden === true &&
    detail.hiddenErrorNodes &&
    container &&
    container.offsetParent === null
  )
    detail.hiddenErrorNodes.push(container);
}

function useFieldValidation(
  containerRef: React.RefObject<HTMLDivElement | null>,
  validate: () => string,
) {
  const [error, setError] = useState("");
  const validateRef = useRef(validate);
  validateRef.current = validate;

  useEffect(() => {
    const form = resolveValidationHost(containerRef.current);
    if (!form) return undefined;

    function handleValidate(e: Event) {
      const detail = (e as CustomEvent).detail as
        | {
            errorCount: number;
            includeHidden?: boolean;
            hiddenErrorNodes?: Element[];
          }
        | undefined;

      const includeHidden = detail?.includeHidden === true;
      if (!shouldValidateField(containerRef.current, includeHidden)) return;

      const validationError = validateRef.current();
      setError(validationError);
      if (validationError && detail) {
        detail.errorCount++;
        // An error on a field the visitor cannot currently SEE needs special
        // handling by the caller: it must either jump them to it or let the
        // submit through, because an inline message on a `display:none` field
        // is a dead button. Only reported when this pass opted into hidden
        // fields — a normal pass never validates them at all.
        if (
          includeHidden &&
          detail.hiddenErrorNodes &&
          containerRef.current &&
          containerRef.current.offsetParent === null
        )
          detail.hiddenErrorNodes.push(containerRef.current);
      }
    }

    function handleReset() {
      setError("");
    }

    form.addEventListener(WB_VALIDATE_EVENT, handleValidate);
    form.addEventListener("reset", handleReset);
    return () => {
      form.removeEventListener(WB_VALIDATE_EVENT, handleValidate);
      form.removeEventListener("reset", handleReset);
    };
  }, [containerRef]);

  const clearOrRevalidate = () => {
    if (!error) return;
    const validationError = validateRef.current();
    setError(validationError);
  };

  return { error, setError, clearOrRevalidate };
}

/* ------------------------------------------------------------------ */
/*  DataInputAcceptanceField                                           */
/* ------------------------------------------------------------------ */

function DataInputAcceptanceField({
  fieldVariable,
  session,
  secondaryColor,
  labelColor,
  borderColor,
  rounding,
  readOnly,
  errorColor,
  acceptButtonText,
  declineButtonText,
  selectedButtonColor,
  selectedButtonBackground,
  hoverButtonColor,
  hoverButtonBackground,
  inputFontColor,
  backgroundColor,
}: {
  fieldVariable: string;
  session: unknown;
  secondaryColor: string;
  labelColor: string;
  borderColor: string;
  rounding: string;
  readOnly: boolean;
  errorColor: string;
  acceptButtonText?: string | undefined;
  declineButtonText?: string | undefined;
  selectedButtonColor?: string | undefined;
  selectedButtonBackground?: string | undefined;
  hoverButtonColor?: string | undefined;
  hoverButtonBackground?: string | undefined;
  inputFontColor?: string | undefined;
  backgroundColor?: string | undefined;
}) {
  const sessionValue = resolveSessionPrefillValue(session, fieldVariable);
  // AUDIT 2026-08-14 — normalise, do not pass through. `fieldVariable` is now
  // always `state` (see acceptanceField.ts), and `state` holds UNCONTACTED /
  // CONTACTED / WAITLISTED as well as the two real answers. A raw
  // pass-through renders with no button highlighted while
  // `required && !selected` reads FALSE — a truthy string — so the required
  // check silently passes and the form posts an answer nobody gave.
  //
  // It also fixes what the old key did: prefilling from `acceptanceState`
  // meant an invited guest saw "Zusagen" preselected because the ORGANISER
  // had put them on the list, not because they had answered.
  const initialValue = normalizeAcceptancePrefill(sessionValue);
  const roundedClasses = extractUtilityClasses(rounding, "rounded");

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "0.3rem",
      }}
    >
      <label style={{ fontSize: "0.9rem", color: labelColor }}>
        {t("status")}
        <span style={{ color: errorColor }}> *</span>
      </label>
      <GuestStatusInput
        initialValue={initialValue}
        secondaryColor={secondaryColor}
        inputFontColor={inputFontColor}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        roundedClasses={roundedClasses}
        resolvedRounded={rounding}
        effectiveBorder={true}
        acceptButtonText={acceptButtonText || t("yes")}
        declineButtonText={declineButtonText || t("no")}
        selectedButtonColor={selectedButtonColor}
        selectedButtonBackground={selectedButtonBackground}
        hoverButtonColor={hoverButtonColor}
        hoverButtonBackground={hoverButtonBackground}
        errorColor={errorColor}
        sharedAttrs={{
          required: !readOnly,
          disabled: readOnly,
          "data-field-variable": fieldVariable,
          "data-companion": "false",
          "data-companion-index": "0",
        }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  GuestStatusInput                                                   */
/* ------------------------------------------------------------------ */

function GuestStatusInput({
  initialValue,
  secondaryColor,
  inputFontColor,
  backgroundColor,
  borderColor,
  roundedClasses,
  resolvedRounded,
  effectiveBorder,
  acceptButtonText,
  declineButtonText,
  selectedButtonColor,
  selectedButtonBackground,
  hoverButtonColor,
  hoverButtonBackground,
  errorColor = "#ef4444",
  sharedAttrs,
}: {
  initialValue: string;
  secondaryColor: string;
  inputFontColor?: string | undefined;
  backgroundColor?: string | undefined;
  borderColor: string;
  roundedClasses: string;
  resolvedRounded: string;
  effectiveBorder: boolean;
  acceptButtonText: string;
  declineButtonText: string;
  selectedButtonColor?: string | undefined;
  selectedButtonBackground?: string | undefined;
  hoverButtonColor?: string | undefined;
  hoverButtonBackground?: string | undefined;
  errorColor?: string;
  sharedAttrs: {
    required: boolean;
    disabled: boolean;
    "data-field-variable": string;
    "data-field-label"?: string;
    "data-companion": "true" | "false";
    "data-companion-index": string;
  };
}) {
  const [selected, setSelected] = useState<string>(initialValue);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const acceptBtnRef = useRef<HTMLButtonElement>(null);
  const declineBtnRef = useRef<HTMLButtonElement>(null);

  const { error, clearOrRevalidate } = useFieldValidation(containerRef, () => {
    if (sharedAttrs.disabled) return "";
    if (sharedAttrs.required && !selected) return t("statusRequired");
    return "";
  });

  function handleClick(value: "ACCEPTED" | "DECLINED") {
    if (sharedAttrs.disabled) return;
    setSelected((prev) => {
      const next = prev === value && !sharedAttrs.required ? "" : value;
      return next;
    });
  }

  // Clear error and notify visibility when selection changes
  useEffect(() => {
    clearOrRevalidate();
    notifyFieldChanged(
      sharedAttrs["data-field-variable"],
      selected,
      sharedAttrs["data-companion"] === "true",
      sharedAttrs["data-companion-index"],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // P9-F1: rule-hide unmount clears the live value (this component always
  // mount-notifies above, so the unconditional clear stays symmetric —
  // StrictMode's remount re-notifies immediately).
  useEffect(() => {
    return () =>
      notifyFieldChanged(
        sharedAttrs["data-field-variable"],
        "",
        sharedAttrs["data-companion"] === "true",
        sharedAttrs["data-companion-index"],
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hover state via native DOM listeners
  useEffect(() => {
    const acceptEl = acceptBtnRef.current;
    const declineEl = declineBtnRef.current;
    if (!acceptEl || !declineEl) return;

    const enterAccept = () => setHoveredButton("ACCEPTED");
    const enterDecline = () => setHoveredButton("DECLINED");
    const leave = () => setHoveredButton(null);

    acceptEl.addEventListener("mouseenter", enterAccept);
    acceptEl.addEventListener("mouseleave", leave);
    declineEl.addEventListener("mouseenter", enterDecline);
    declineEl.addEventListener("mouseleave", leave);

    return () => {
      acceptEl.removeEventListener("mouseenter", enterAccept);
      acceptEl.removeEventListener("mouseleave", leave);
      declineEl.removeEventListener("mouseenter", enterDecline);
      declineEl.removeEventListener("mouseleave", leave);
    };
  }, []);

  const borderRadius = roundedClasses
    ? undefined
    : toCssLength(resolvedRounded);

  const defaultTextColor = inputFontColor || secondaryColor;
  const defaultBg = backgroundColor || "transparent";
  const resolvedSelectedBg = selectedButtonBackground || secondaryColor + "1a";
  const resolvedSelectedColor = selectedButtonColor || defaultTextColor;
  const resolvedHoverBg = hoverButtonBackground || secondaryColor + "0d";
  const resolvedHoverColor = hoverButtonColor || defaultTextColor;

  return (
    <div ref={containerRef}>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="hidden"
          name={sharedAttrs["data-field-variable"]}
          value={selected}
          {...sharedAttrs}
        />
        {(
          [
            ["ACCEPTED", acceptButtonText, acceptBtnRef],
            ["DECLINED", declineButtonText, declineBtnRef],
          ] as const
        ).map(([val, text, ref]) => {
          const isSelected = selected === val;
          const isHovered = hoveredButton === val;

          let bg = defaultBg;
          let color = defaultTextColor;
          if (isSelected) {
            bg = resolvedSelectedBg;
            color = resolvedSelectedColor;
          } else if (isHovered) {
            bg = resolvedHoverBg;
            color = resolvedHoverColor;
          }

          return (
            <button
              key={val}
              ref={ref}
              type="button"
              onClick={() => {
                handleClick(val);
              }}
              disabled={sharedAttrs.disabled}
              className={classNames(roundedClasses)}
              style={{
                padding: "0.5rem 1rem",
                cursor: sharedAttrs.disabled ? "not-allowed" : "pointer",
                backgroundColor: error ? bg : bg,
                color: error ? errorColor : color,
                border: error
                  ? `1px solid ${errorColor}`
                  : effectiveBorder
                    ? `1px solid ${isSelected ? secondaryColor : borderColor}`
                    : "1px solid " + borderColor,
                borderRadius,
                fontWeight: 500,
                transition: "all 0.15s ease",
                ...(error
                  ? { boxShadow: `0 0 0 1px ${errorColor}30` }
                  : {}),
              }}
            >
              {text}
            </button>
          );
        })}
      </div>
      {error ? (
        <p
          style={{
            margin: "0.25rem 0 0",
            fontSize: "0.75rem",
            color: errorColor,
          }}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  ImageUploadInput — IMAGE additional fields (Phase 2, IMAGE spec)   */
/* ------------------------------------------------------------------ */

/**
 * File-pick widget for IMAGE-typed additional fields. Uploads the picked
 * file to POST /api/runtime/af-image-upload immediately (5 MB / type check
 * client-side first), keeps the returned pendingId in a hidden input
 * (`data-image-pending`) that collectFieldValues wraps into the
 * `{__pendingUpload: <id>}` sentinel on submit. The worker verifies the
 * pending row and promotes the object after the guest write — the stored
 * value is never client-writable.
 *
 * Stored-image state (data-input): when the session prefill carries a
 * stored value (`hasStoredImage`), an untouched widget shows a "Bild
 * gespeichert" chip and submits NOTHING (collectFieldValues omits the ""
 * key — absent = keep). The chip's explicit remove button flips the hidden
 * input to IMAGE_CLEAR_SENTINEL, which submits the literal `null` clear.
 * Clearing a fresh (not yet submitted) upload just returns to the stored
 * state — the stored image is only deleted on an explicit remove + submit.
 */
function ImageUploadInput({
  inputClassName,
  inputStyle,
  placeholder,
  errorColor,
  hasStoredImage,
  sharedAttrs,
  onChanged,
}: {
  inputClassName: string | undefined;
  inputStyle: CSSProperties;
  placeholder: string | undefined;
  errorColor: string;
  /** Session prefill says the guest already has a stored image. */
  hasStoredImage: boolean;
  sharedAttrs: {
    readonly required: boolean;
    readonly disabled: boolean;
    readonly "data-field-variable": string;
    readonly "data-field-label": string;
    readonly "data-companion": "true" | "false";
    readonly "data-companion-index": string;
  };
  /** Parent FormInputInner's handleChange — re-reads the hidden input from
   *  the DOM, so it must run AFTER the commit (effect below). */
  onChanged: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [pendingId, setPendingId] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [localError, setLocalError] = useState("");
  // Guest explicitly removed the STORED image (chip's remove button).
  const [storedCleared, setStoredCleared] = useState(false);
  const disabled = sharedAttrs.disabled;
  const showStoredChip =
    hasStoredImage && !storedCleared && !pendingId && !uploading;

  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  // Never notify on mount — an untouched field must not shadow session
  // values in the live-values map (mirrors the text inputs' behavior).
  const touchedRef = useRef(false);
  useEffect(() => {
    if (!touchedRef.current) return;
    onChangedRef.current();
  }, [pendingId, storedCleared]);

  // Hidden-input value → collectFieldValues:
  //   pendingId   → {__pendingUpload} sentinel
  //   __CLEAR__   → literal null (explicit stored-image remove)
  //   ""          → key OMITTED (untouched)
  const hiddenValue = pendingId
    ? pendingId
    : hasStoredImage && storedCleared
      ? IMAGE_CLEAR_SENTINEL
      : "";

  // Revoke the previous objectURL on replace + unmount.
  useEffect(() => {
    if (!previewUrl) return undefined;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  async function handlePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset so re-picking the same file re-fires change.
    e.target.value = "";
    if (!file) return;
    const fileError = validateImageFile(file);
    if (fileError !== null) {
      setLocalError(
        fileError === "tooBig"
          ? t("imageTooBig", String(MAX_PUBLIC_AF_IMAGE_BYTES / (1024 * 1024)))
          : t("imageWrongType"),
      );
      return;
    }
    setLocalError("");
    setUploading(true);
    touchedRef.current = true;
    try {
      const id = await uploadAfImage(file, sharedAttrs["data-field-variable"]);
      setPreviewUrl(URL.createObjectURL(file));
      setPendingId(id);
    } catch {
      setPendingId("");
      setPreviewUrl("");
      setLocalError(t("imageUploadFailed"));
    } finally {
      setUploading(false);
    }
  }

  // Clear a FRESH upload — falls back to the stored image (if any), which
  // stays untouched server-side.
  function handleClear() {
    touchedRef.current = true;
    setPendingId("");
    setPreviewUrl("");
    setLocalError("");
  }

  // Explicit remove of the STORED image → submit the literal null clear.
  function handleRemoveStored() {
    touchedRef.current = true;
    setStoredCleared(true);
    setLocalError("");
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
      {/* FIRST input in the container so readInputValue picks it up. */}
      <input
        type="hidden"
        value={hiddenValue}
        data-image-pending="true"
        data-image-stored={showStoredChip ? "true" : "false"}
        readOnly
        {...sharedAttrs}
      />
      <div
        className={inputClassName}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={() => {
          if (!disabled && !uploading) fileRef.current?.click();
        }}
        onKeyDown={(e) => {
          if (disabled || uploading) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            fileRef.current?.click();
          }
        }}
        style={{
          ...inputStyle,
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
          cursor: disabled ? "not-allowed" : "pointer",
          userSelect: "none",
        }}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt=""
            style={{
              width: "2rem",
              height: "2rem",
              objectFit: "cover",
              borderRadius: "0.25rem",
              flexShrink: 0,
            }}
          />
        ) : null}
        {showStoredChip ? (
          // "Bild gespeichert" chip — the guest's stored image exists
          // server-side; untouched, it is kept (nothing submitted).
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "0.1rem 0.5rem",
              borderRadius: "9999px",
              border: "1px solid currentColor",
              fontSize: "0.8em",
              opacity: 0.8,
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {t("imageStored")}
          </span>
        ) : null}
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            opacity: previewUrl || uploading || showStoredChip ? 1 : 0.5,
          }}
        >
          {uploading
            ? t("imageUploading")
            : previewUrl || showStoredChip
              ? t("imageReplace")
              : placeholder || t("imageSelect")}
        </span>
        {uploading ? (
          <span
            aria-hidden="true"
            className="animate-spin"
            style={{
              width: "1rem",
              height: "1rem",
              border: "2px solid currentColor",
              borderTopColor: "transparent",
              borderRadius: "9999px",
              display: "inline-block",
              flexShrink: 0,
            }}
          />
        ) : null}
        {(pendingId || showStoredChip) && !uploading && !disabled ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              // Fresh upload → drop the staged file (stored image, if any,
              // is untouched). Stored chip → explicit remove (submits null).
              if (pendingId) handleClear();
              else handleRemoveStored();
            }}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: "inherit",
              opacity: 0.7,
              textDecoration: "underline",
              fontSize: "0.85em",
              flexShrink: 0,
            }}
          >
            {t("imageRemove")}
          </button>
        ) : null}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept={IMAGE_FILE_ACCEPT}
        style={{ display: "none" }}
        onChange={(e) => {
          void handlePick(e);
        }}
        disabled={disabled}
        aria-hidden="true"
        tabIndex={-1}
      />
      {localError ? (
        <p style={{ margin: 0, fontSize: "0.75rem", color: errorColor }}>
          {localError}
        </p>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  FormInput                                                          */
/* ------------------------------------------------------------------ */

export function FormInput({
  isVisible = true,
  label: labelRaw,
  placeholder: placeholderRaw,
  overwriteType,
  inputFieldVariable = "email",
  requiredField = false,
  multiline = false,
  multilineRows = 8,
  border,
  borderColor,
  rounded,
  backgroundColor,
  borderStyle,
  borderWidth,
  borderSides,
  inputFontColor,
  labelFontColor,
  requiredColor,
  fontFamily,
  fontSize,
  gridArea = "col-span-6",
  chapter: chapterRaw,
  chapterSpacingTop,
  chapterSpacingBottom,
  chapterFontColor,
  errorColor,
  minValue,
  maxValue,
  defaultValue,
  value,
  type,
  companionIndex = 0,
  acceptButtonText: acceptButtonTextRaw,
  declineButtonText: declineButtonTextRaw,
  selectedButtonColor,
  selectedButtonBackground,
  hoverButtonColor,
  hoverButtonBackground,
}: {
  isVisible?: boolean;
  label?: LangText;
  placeholder?: LangText;
  overwriteType?: DataType;
  inputFieldVariable?: string;
  requiredField?: boolean;
  multiline?: boolean;
  multilineRows?: number;
  border?: boolean;
  borderColor?: string;
  rounded?: string;
  backgroundColor?: string;
  borderStyle?: string;
  borderWidth?: string;
  borderSides?: BorderSide[];
  inputFontColor?: string;
  labelFontColor?: string;
  requiredColor?: string;
  fontFamily?: string;
  fontSize?: string;
  gridArea?: string;
  chapter?: LangText;
  chapterSpacingTop?: string;
  chapterSpacingBottom?: string;
  chapterFontColor?: string;
  errorColor?: string;
  minValue?: string;
  maxValue?: string;
  defaultValue?: string;
  value?: string;
  type?: "slotting" | "companion";
  companionIndex?: number;
  acceptButtonText?: LangText;
  declineButtonText?: LangText;
  selectedButtonColor?: string;
  selectedButtonBackground?: string;
  hoverButtonColor?: string;
  hoverButtonBackground?: string;
}) {
  const {
    formInputTheme,
    currentColorPalette,
    currentFonts,
    currentRounding,
    fields,
    session,
  } = useWBTheme();
  const { currentLang, primaryLanguage } = useWBLang();
  const label = resolveText(labelRaw, currentLang, primaryLanguage);
  const placeholder = resolveText(placeholderRaw, currentLang, primaryLanguage);
  const chapter = resolveText(chapterRaw, currentLang, primaryLanguage);
  const acceptButtonText =
    resolveText(acceptButtonTextRaw, currentLang, primaryLanguage) || undefined;
  const declineButtonText =
    resolveText(declineButtonTextRaw, currentLang, primaryLanguage) || undefined;
  const eventTz = useEventTimezone();
  const draftSnapshot = useDataInputDraftSnapshot();
  const companionIndexOverride = useCompanionIndexOverride();
  const effectiveCompanionIndex = companionIndexOverride ?? companionIndex;
  const field = fields.find((it) => it.name === inputFieldVariable);
  const effectiveType = fieldTypeFromProps(overwriteType, field, inputFieldVariable);
  const isAcceptanceField =
    inputFieldVariable === "state" || inputFieldVariable === "acceptanceState";

  // Field-definition conditions (set by the event owner in the admin builder).
  // Evaluated live against the guest's current form values. Wrap the flat live
  // map under `additionalFields` too so `additionalFields.*` condition keys
  // resolve. The website-builder-backend re-enforces all three at submit.
  const liveGuestValues = useLiveGuestValues();
  // Build the evaluator context so BOTH built-in keys (top-level, e.g.
  // firstName) and additional-field keys resolve. `liveGuestValues` carries
  // built-ins + live-TYPED values bare at the top level, plus the session
  // guest's PREFILLED additional fields still nested under `additionalFields`
  // (session.user.additionalFields). Merge both sources into one
  // `additionalFields` object so `additionalFields.<name>` keys resolve whether
  // the value was prefilled or just typed. (A naive `additionalFields:
  // liveGuestValues` would shadow the prefilled nested map and lose it.)
  const sessionAdditional =
    liveGuestValues.additionalFields &&
    typeof liveGuestValues.additionalFields === "object" &&
    !Array.isArray(liveGuestValues.additionalFields)
      ? (liveGuestValues.additionalFields as Record<string, unknown>)
      : {};
  const liveAdditional: Record<string, unknown> = {};
  for (const f of fields) {
    if (
      f.additionalField &&
      Object.prototype.hasOwnProperty.call(liveGuestValues, f.name)
    ) {
      liveAdditional[f.name] = liveGuestValues[f.name];
    }
  }
  // Audit 2026-09-23 (4c): the Worker enforces these conditions on the
  // edge-normalized value AND the edge-normalized operand
  // (`data-input-split.ts`, `condition-operands.ts`). Preview them the same
  // way, or a `"DE "` typed here / saved there hides a field the submit
  // then expects. `state` keeps its exact spelling (enum guard).
  const conditionData = normalizeConditionData({
    ...liveGuestValues,
    additionalFields: { ...sessionAdditional, ...liveAdditional },
  });
  const conditionHidden =
    !!field?.visibilityCondition &&
    !evaluateConditionGroup(
      normalizedConditionTree(field.visibilityCondition),
      conditionData,
    );
  const conditionLocked =
    !!field?.editableCondition &&
    !evaluateConditionGroup(
      normalizedConditionTree(field.editableCondition),
      conditionData,
    );

  // This field's own value as the condition evaluator / submit see it: direct
  // key first, then the `additionalFields.*` fallback (mirrors resolveValue's
  // lookup order). Used by the stale-selection + hidden-clear reconcilers
  // below. NOTE: the `if (!isVisible) / isAcceptanceField` early returns used
  // to sit here — they were moved BELOW the last hook so every hook runs
  // unconditionally (React rules-of-hooks).
  const ownRawValue =
    (Object.prototype.hasOwnProperty.call(conditionData, inputFieldVariable)
      ? (conditionData as Record<string, unknown>)[inputFieldVariable]
      : undefined) ??
    (conditionData.additionalFields as Record<string, unknown>)[
      inputFieldVariable
    ];
  const ownStr = ownRawValue == null ? "" : String(ownRawValue);
  const ownHasValue =
    ownRawValue != null &&
    !(Array.isArray(ownRawValue) && ownRawValue.length === 0) &&
    ownStr !== "";

  const resolvedFormInputTheme = resolveFormInputTheme({
    formInputTheme,
    palette: currentColorPalette,
    fonts: currentFonts,
    currentRounding,
  });
  const effectiveBorder = border ?? resolvedFormInputTheme.border;
  const resolvedBorderWidth = borderWidth ?? resolvedFormInputTheme.borderWidth;
  const resolvedBorderStyle = borderStyle ?? resolvedFormInputTheme.borderStyle;
  const resolvedBorderSides =
    borderSides ?? (resolvedFormInputTheme.borderSides as BorderSide[]);
  const resolvedRounded = rounded ?? resolvedFormInputTheme.borderRadius;
  const resolvedLabelFontColor =
    labelFontColor ?? resolvedFormInputTheme.labelFontColor;
  const resolvedChapterFontColor = chapterFontColor ?? resolvedLabelFontColor;
  const resolvedInputFontColor =
    inputFontColor ?? resolvedFormInputTheme.inputFontColor;
  const resolvedErrorColor =
    errorColor ?? resolvedFormInputTheme.errorColor ?? "#ef4444";
  const resolvedRequiredColor = requiredColor ?? resolvedErrorColor;
  const resolvedFontFamily = resolveBodyFontFamily({
    override: fontFamily ?? resolvedFormInputTheme.fontFamily,
    fonts: currentFonts,
  });
  const resolvedFontSize = fontSize ?? resolvedFormInputTheme.fontSize;
  const resolvedBackgroundColor =
    backgroundColor ?? resolvedFormInputTheme.backgroundColor;
  const resolvedBorderColor = borderColor ?? resolvedFormInputTheme.borderColor;

  const borderClasses = getBorderClasses(
    effectiveBorder,
    resolvedBorderWidth,
    resolvedBorderStyle,
    resolvedBorderSides,
  );
  const roundedClasses = extractUtilityClasses(resolvedRounded, "rounded");
  const commonStyle = buildInputCommonStyle(
    resolvedBackgroundColor,
    resolvedInputFontColor,
    resolvedFontFamily,
    toCssLength(resolvedFontSize),
  );

  const selectOptions = field?.options ?? [];
  // Per-option conditions (dependent dropdowns): keep only options whose
  // condition is met for the guest's current values. Unkeyed options always
  // pass (evaluateConditionGroup returns true for undefined). conditionData is
  // a fresh object each render, so this intentionally re-derives live as
  // sibling values change.
  const availableSelectOptions = field?.optionConditions
    ? selectOptions.filter((o) =>
        evaluateConditionGroup(
          normalizedConditionTree(field.optionConditions?.[o]),
          conditionData,
        ),
      )
    : selectOptions;
  // Alphabetical display order for SINGLE/MULTI selects. The runtime shows the
  // raw option value as its label (no translation here), so sorting on the raw
  // value sorts by what the guest reads. Locale-aware for accents/umlauts.
  // Only user-defined option lists sort; built-in semantic enums keep order.
  const sortedSelectOptions = useMemo(
    () =>
      field?.additionalField
        ? sortOptionsForDisplay(availableSelectOptions, undefined, getLocale())
        : availableSelectOptions,
    [field?.additionalField, availableSelectOptions],
  );
  const isCompanion = type === "companion";
  const dataInputReadOnly = useDataInputReadOnly();

  // Stale selected option. When optionConditions removed the currently
  // selected option(s) (dependent dropdown), the uncontrolled <select> DOM
  // auto-resets, but App's live-values map keeps the old token (no change
  // event fires), so chained conditions would still see it. Clear/trim the
  // live token once. Idempotent — only acts when a live token is no longer
  // offered, so it cannot re-fire after reconciling. SINGLE clears to "";
  // MULTI drops only the removed tokens and keeps the still-valid ones.
  const isSelectField =
    effectiveType === DataType.SINGLE_SELECT ||
    effectiveType === DataType.MULTI_SELECT;
  const availableOptionsKey = availableSelectOptions.join("\n");
  useEffect(() => {
    if (conditionHidden || !isSelectField) return;
    const allowed = new Set(availableSelectOptions);
    if (effectiveType === DataType.SINGLE_SELECT) {
      if (ownStr && !allowed.has(ownStr)) {
        notifyFieldChanged(
          inputFieldVariable,
          "",
          isCompanion,
          String(effectiveCompanionIndex),
        );
      }
      return;
    }
    const tokens = Array.isArray(ownRawValue)
      ? ownRawValue.map(String)
      : ownStr
        ? ownStr.split(",")
        : [];
    const valid = tokens.filter((tkn) => allowed.has(tkn));
    if (valid.length !== tokens.length) {
      notifyFieldChanged(
        inputFieldVariable,
        valid.join(","),
        isCompanion,
        String(effectiveCompanionIndex),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableOptionsKey, ownStr, effectiveType, conditionHidden]);

  // A prefilled (session) value of a now-hidden field would linger in the
  // live-values map and let downstream chained conditions evaluate against a
  // hidden field's value. Mirror the typed-value unmount-clear: notify empty
  // ONCE per hidden transition, and only when there is actually a value to
  // clear (keeps it idempotent + avoids needlessly shadowing session values).
  const hiddenClearedRef = useRef(false);
  useEffect(() => {
    if (!(conditionHidden && isVisible && !isAcceptanceField)) {
      hiddenClearedRef.current = false;
      return;
    }
    if (hiddenClearedRef.current || !ownHasValue) return;
    hiddenClearedRef.current = true;
    notifyFieldChanged(
      inputFieldVariable,
      "",
      isCompanion,
      String(effectiveCompanionIndex),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditionHidden, isVisible, isAcceptanceField, ownHasValue]);

  // Rules-of-hooks: these early returns were moved here from just after the
  // condition computation so that every hook above runs unconditionally on
  // every render (the prior placement sat above useMemo / useDataInputReadOnly
  // / the reconciler effects).
  if (!isVisible) return null;
  if (isAcceptanceField) return null;

  const inputSizeClasses = extractUtilityClasses(resolvedFontSize, "text-");
  const inputClassName = classNames(
    "px-3 py-2 w-full placeholder:opacity-50 focus:outline-hidden",
    inputSizeClasses,
    borderClasses,
    roundedClasses,
  );
  const fieldDefaultValue =
    field?.defaultValue !== undefined &&
    field?.defaultValue !== null &&
    field?.defaultValue !== false
      ? field.defaultValue
      : undefined;
  const draftDefaultValue = resolveDraftPrefillValue(
    draftSnapshot,
    inputFieldVariable,
    isCompanion,
    effectiveCompanionIndex,
  );
  const sessionDefaultValue = isCompanion
    ? resolveCompanionSessionPrefillValue(
        session,
        inputFieldVariable,
        effectiveCompanionIndex,
      )
    : resolveSessionPrefillValue(session, inputFieldVariable);
  // IMAGE prefill is deliberately NOT fed into the input value (see
  // normalizeInputDefaultValue) — but its PRESENCE drives the widget's
  // "Bild gespeichert" chip + explicit remove, and lets the required
  // check pass while the stored image is kept (submission omits the key).
  const hasStoredImage =
    effectiveType === DataType.IMAGE && isStoredImageValue(sessionDefaultValue);
  const initialValue = normalizeInputDefaultValue(
    value ??
      defaultValue ??
      draftDefaultValue ??
      sessionDefaultValue ??
      fieldDefaultValue,
    effectiveType,
  );
  const rawInitialScalarValue = Array.isArray(initialValue)
    ? (initialValue[0] ?? "")
    : (initialValue ?? "");
  // For DATE / DATE_TIME, convert any UTC ISO pre-fill into a wall-clock
  // string in the event tz before feeding the native HTML5 input. Empty
  // strings, non-ISO values, and other types pass through unchanged.
  const initialScalarValue =
    effectiveType === DataType.DATE || effectiveType === DataType.DATE_TIME
      ? toWallInputValue(String(rawInitialScalarValue), effectiveType, eventTz)
      : (effectiveType === DataType.DOUBLE ||
            effectiveType === DataType.CURRENCY) &&
          rawInitialScalarValue != null &&
          String(rawInitialScalarValue) !== "" &&
          Number.isFinite(Number(rawInitialScalarValue))
        ? // Prefill the canonical number in the guest's LOCALE editable form
          // (locale decimal, no grouping) so an unchanged blur re-parses back to
          // the same canonical value (raw "12.345" would otherwise become 12345).
          toEditableLocaleString(Number(rawInitialScalarValue), currentLang)
        : rawInitialScalarValue;
  const initialMultiValue = Array.isArray(initialValue)
    ? initialValue
    : initialScalarValue
      ? [initialScalarValue]
      : [];
  const normalizedMin =
    minValue && minValue.trim().length > 0
      ? effectiveType === DataType.DATE || effectiveType === DataType.DATE_TIME
        ? toWallInputValue(minValue, effectiveType, eventTz)
        : minValue
      : undefined;
  const normalizedMax =
    maxValue && maxValue.trim().length > 0
      ? effectiveType === DataType.DATE || effectiveType === DataType.DATE_TIME
        ? toWallInputValue(maxValue, effectiveType, eventTz)
        : maxValue
      : undefined;

  // Editable condition unmet → lock the input (public path is strict; unlike
  // the admin form's soft-grey-but-overridable treatment).
  const effectiveReadOnly = dataInputReadOnly || conditionLocked;
  // A field is required if the builder marked the block required OR — for
  // BUILT-IN fields only — the field def is required (a built-in made required
  // via event.fieldRequired → synthesized into the field def). Custom fields
  // keep the builder's block-prop behaviour (changing that would newly block
  // existing open registrations whose custom required fields aren't marked
  // required on the block).
  const effectiveRequired =
    requiredField || (field?.additionalField === false && !!field?.required);
  const sharedAttrs = {
    required: effectiveRequired && !effectiveReadOnly,
    disabled: effectiveReadOnly,
    "data-field-variable": inputFieldVariable,
    "data-field-label": label || inputFieldVariable,
    "data-companion": isCompanion ? "true" : "false",
    "data-companion-index": String(effectiveCompanionIndex),
  } as const;

  // Visibility unmet → hide the field entirely (its value is stripped server-
  // side on submit, mirroring the admin/main-frontend behavior). Placed AFTER
  // all hooks so this reactive toggle never changes the hook count mid-life.
  if (conditionHidden) return null;

  return (
    <FormInputInner
      effectiveType={effectiveType}
      field={field}
      requiredField={effectiveRequired}
      dataInputReadOnly={effectiveReadOnly}
      resolvedErrorColor={resolvedErrorColor}
      resolvedRequiredColor={resolvedRequiredColor}
      resolvedBorderColor={resolvedBorderColor}
      resolvedLabelFontColor={resolvedLabelFontColor}
      resolvedChapterFontColor={resolvedChapterFontColor}
      effectiveBorder={effectiveBorder}
      roundedClasses={roundedClasses}
      resolvedRounded={resolvedRounded}
      inputClassName={inputClassName}
      fontSizeClasses={inputSizeClasses}
      commonStyle={commonStyle}
      gridArea={gridArea}
      chapter={chapter}
      chapterSpacingTop={chapterSpacingTop}
      chapterSpacingBottom={chapterSpacingBottom}
      label={label}
      placeholder={placeholder}
      multiline={multiline}
      multilineRows={multilineRows}
      initialScalarValue={initialScalarValue}
      initialMultiValue={initialMultiValue}
      normalizedMin={normalizedMin}
      normalizedMax={normalizedMax}
      eventTz={eventTz}
      formLocale={currentLang}
      // P9-F2 drive-by (pre-existing break, verified present on baseline
      // main): the sorted list was computed here but the Inner's JSX
      // referenced the out-of-scope `sortedSelectOptions` name directly —
      // a ReferenceError on every SINGLE/MULTI_SELECT render. Pass the
      // sorted list through the (previously unused) prop.
      selectOptions={sortedSelectOptions}
      hasStoredImage={hasStoredImage}
      sharedAttrs={sharedAttrs}
      secondaryColor={currentColorPalette.secondary}
      resolvedInputFontColor={resolvedInputFontColor}
      resolvedBackgroundColor={resolvedBackgroundColor}
      acceptButtonText={acceptButtonText}
      declineButtonText={declineButtonText}
      selectedButtonColor={selectedButtonColor}
      selectedButtonBackground={selectedButtonBackground}
      hoverButtonColor={hoverButtonColor}
      hoverButtonBackground={hoverButtonBackground}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  FormInputInner — separated so hooks run unconditionally            */
/* ------------------------------------------------------------------ */

function FormInputInner({
  effectiveType,
  field,
  requiredField,
  dataInputReadOnly,
  resolvedErrorColor,
  resolvedRequiredColor,
  resolvedBorderColor,
  resolvedLabelFontColor,
  resolvedChapterFontColor,
  effectiveBorder,
  roundedClasses,
  resolvedRounded,
  inputClassName,
  fontSizeClasses,
  commonStyle,
  gridArea,
  chapter,
  chapterSpacingTop,
  chapterSpacingBottom,
  label,
  placeholder,
  multiline,
  multilineRows,
  initialScalarValue,
  initialMultiValue,
  normalizedMin,
  normalizedMax,
  eventTz,
  formLocale,
  selectOptions,
  hasStoredImage,
  sharedAttrs,
  secondaryColor,
  resolvedInputFontColor,
  resolvedBackgroundColor,
  acceptButtonText,
  declineButtonText,
  selectedButtonColor,
  selectedButtonBackground,
  hoverButtonColor,
  hoverButtonBackground,
}: {
  effectiveType: DataType;
  field: Field | undefined;
  requiredField: boolean;
  dataInputReadOnly: boolean;
  resolvedErrorColor: string;
  resolvedRequiredColor: string;
  resolvedBorderColor: string;
  resolvedLabelFontColor: string;
  resolvedChapterFontColor: string;
  effectiveBorder: boolean;
  roundedClasses: string;
  resolvedRounded: string;
  inputClassName: string | undefined;
  fontSizeClasses: string | undefined;
  commonStyle: CSSProperties;
  gridArea: string;
  chapter: string | undefined;
  chapterSpacingTop: string | undefined;
  chapterSpacingBottom: string | undefined;
  label: string | undefined;
  placeholder: string | undefined;
  multiline: boolean;
  multilineRows: number;
  initialScalarValue: string;
  initialMultiValue: string[];
  normalizedMin: string | undefined;
  normalizedMax: string | undefined;
  eventTz: string;
  /** Guest-facing form language (e.g. "de") for locale-aware number parsing. */
  formLocale: string;
  selectOptions: string[];
  /** IMAGE fields: the session prefill carries a stored image value. */
  hasStoredImage: boolean;
  sharedAttrs: {
    readonly required: boolean;
    readonly disabled: boolean;
    readonly "data-field-variable": string;
    readonly "data-field-label": string;
    readonly "data-companion": "true" | "false";
    readonly "data-companion-index": string;
  };
  secondaryColor: string;
  resolvedInputFontColor: string;
  resolvedBackgroundColor: string;
  acceptButtonText: string | undefined;
  declineButtonText: string | undefined;
  selectedButtonColor: string | undefined;
  selectedButtonBackground: string | undefined;
  hoverButtonColor: string | undefined;
  hoverButtonBackground: string | undefined;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  const { error, clearOrRevalidate } = useFieldValidation(containerRef, () => {
    if (dataInputReadOnly) return "";
    const val = containerRef.current
      ? readInputValue(containerRef.current, effectiveType)
      : "";
    return validateFieldValue(
      val,
      effectiveType,
      requiredField,
      field,
      eventTz,
      normalizedMin,
      normalizedMax,
      containerRef.current ? hasUnparsableNumber(containerRef.current) : false,
    );
  });

  // P9-F1: when a visibility rule hides this field (the engine UNMOUNTS the
  // node), its last typed value must leave App's live-values map so CHAINED
  // rules ("show C when B …") re-evaluate against "" — mirroring the
  // server-side fixpoint strip. Guarded: a never-touched field must NOT
  // write "" (it would shadow session.user values in nodeVisibilityFn's
  // merge, and StrictMode's simulated unmount would do that on every
  // initial mount).
  const hasNotifiedRef = useRef(false);
  const fieldVariableRef = useRef(sharedAttrs["data-field-variable"]);
  fieldVariableRef.current = sharedAttrs["data-field-variable"];
  const companionAttrsRef = useRef({
    companion: sharedAttrs["data-companion"] === "true",
    index: sharedAttrs["data-companion-index"],
  });
  companionAttrsRef.current = {
    companion: sharedAttrs["data-companion"] === "true",
    index: sharedAttrs["data-companion-index"],
  };
  useEffect(() => {
    return () => {
      if (hasNotifiedRef.current)
        notifyFieldChanged(
          fieldVariableRef.current,
          "",
          companionAttrsRef.current.companion,
          companionAttrsRef.current.index,
        );
    };
  }, []);

  const handleChange = () => {
    clearOrRevalidate();
    const val = containerRef.current
      ? readInputValue(containerRef.current, effectiveType)
      : "";
    hasNotifiedRef.current = true;
    notifyFieldChanged(
      sharedAttrs["data-field-variable"],
      val,
      sharedAttrs["data-companion"] === "true",
      sharedAttrs["data-companion-index"],
    );
  };

  // DOUBLE / CURRENCY: parse the locale draft to a canonical dot-decimal and
  // stash it in `data-canonical` BEFORE handleChange runs (readInputValue then
  // reads the canonical for validation + the live-value event). The visible
  // input keeps showing what the guest typed; only the canonical is submitted.
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseLocaleNumber(e.target.value, formLocale);
    e.target.dataset.canonical = parsed !== null ? String(parsed) : "";
    // M23: the canonical alone cannot tell "nothing typed" from "typed
    // something unparsable" — both are "". Validation short-circuits on the
    // empty canonical (`if (!value) return ""`), so garbage produced NO error
    // and the field submitted empty: the guest's "abc" simply vanished. The
    // canonical stays "" so garbage is never submitted; this flag carries the
    // distinction instead.
    if (parsed === null && e.target.value.trim() !== "") {
      e.target.dataset.numberInvalid = "true";
    } else {
      delete e.target.dataset.numberInvalid;
    }
    handleChange();
  };

  // Error-aware border styles
  const activeBorderColor = error ? resolvedErrorColor : resolvedBorderColor;
  const errorBorderProp = error
    ? (effectiveBorder ? undefined : `1px solid ${resolvedErrorColor}`)
    : (effectiveBorder ? undefined : "none");
  const errorShadow = error
    ? { boxShadow: `0 0 0 1px ${resolvedErrorColor}30` }
    : {};
  const inputBorderStyle: CSSProperties = {
    ...commonStyle,
    borderColor: activeBorderColor,
    border: errorBorderProp,
    borderRadius: roundedClasses ? undefined : toCssLength(resolvedRounded),
    ...errorShadow,
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  };

  return (
    <div
      ref={containerRef}
      className={classNames(
        gridArea,
        chapter ? chapterSpacingTop : undefined,
        chapter ? chapterSpacingBottom : undefined,
      )}
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "0.3rem",
      }}
    >
      {chapter ? (
        <label
          className={classNames("w-max", fontSizeClasses)}
          style={{
            color: resolvedChapterFontColor,
            fontFamily: commonStyle.fontFamily,
          }}
        >
          {chapter}
        </label>
      ) : null}
      {label ? (
        <label
          className={classNames("w-max", fontSizeClasses)}
          style={{ color: resolvedLabelFontColor }}
        >
          {label}
          {requiredField ? (
            <span style={{ color: resolvedRequiredColor }}> *</span>
          ) : null}
        </label>
      ) : null}

      {effectiveType === DataType.BOOLEAN ? (
        // `data-boolean-type` lets collectFieldValues submit a real boolean
        // instead of the select's "true"/"false" string — the worker's
        // additional-field validator requires `typeof value === 'boolean'`.
        <select
          className={inputClassName}
          defaultValue={initialScalarValue}
          data-boolean-type="true"
          style={inputBorderStyle}
          onChange={handleChange}
          {...sharedAttrs}
        >
          <option value="">{placeholder}</option>
          <option value="true">{t("yes")}</option>
          <option value="false">{t("no")}</option>
        </select>
      ) : effectiveType === DataType.DATE ? (
        <input
          type="date"
          className={inputClassName}
          placeholder={placeholder}
          min={normalizedMin}
          max={normalizedMax}
          defaultValue={initialScalarValue}
          style={inputBorderStyle}
          onChange={handleChange}
          data-tz-mode="date"
          {...sharedAttrs}
        />
      ) : effectiveType === DataType.DATE_TIME ? (
        <input
          type="datetime-local"
          className={inputClassName}
          placeholder={placeholder}
          min={normalizedMin}
          max={normalizedMax}
          defaultValue={initialScalarValue}
          style={inputBorderStyle}
          onChange={handleChange}
          data-tz-mode="datetime-local"
          {...sharedAttrs}
        />
      ) : effectiveType === DataType.EMAIL ? (
        <input
          type="email"
          className={inputClassName}
          placeholder={placeholder}
          defaultValue={initialScalarValue}
          style={inputBorderStyle}
          onChange={handleChange}
          {...sharedAttrs}
        />
      ) : effectiveType === DataType.URL ? (
        <input
          type="url"
          className={inputClassName}
          placeholder={placeholder}
          defaultValue={initialScalarValue}
          style={inputBorderStyle}
          onChange={handleChange}
          {...sharedAttrs}
        />
      ) : effectiveType === DataType.TIME ? (
        <input
          type="time"
          className={inputClassName}
          placeholder={placeholder}
          min={normalizedMin}
          max={normalizedMax}
          defaultValue={initialScalarValue}
          style={inputBorderStyle}
          onChange={handleChange}
          {...sharedAttrs}
        />
      ) : effectiveType === DataType.LONG ? (
        // Integers: native number input (no decimal/grouping separator needed;
        // grouping a year/ID would corrupt it — LONG is intentionally not
        // locale-grouped). Browser already canonicalises to dot for any locale.
        <input
          type="number"
          className={inputClassName}
          placeholder={placeholder}
          min={normalizedMin}
          max={normalizedMax}
          defaultValue={initialScalarValue}
          style={inputBorderStyle}
          onChange={handleChange}
          {...sharedAttrs}
        />
      ) : effectiveType === DataType.DOUBLE ||
        effectiveType === DataType.CURRENCY ? (
        // Decimals/money: locale text input (de guest types "3,5"). type=number
        // would reject the comma keystroke. `data-numeric-type` + `data-canonical`
        // let readInputValue + collectFieldValues submit the canonical dot value.
        <input
          type="text"
          inputMode="decimal"
          className={inputClassName}
          placeholder={placeholder}
          defaultValue={initialScalarValue}
          data-numeric-type={effectiveType}
          data-canonical={String(
            parseLocaleNumber(String(initialScalarValue ?? ""), formLocale) ?? "",
          )}
          style={inputBorderStyle}
          onChange={handleNumberChange}
          {...sharedAttrs}
        />
      ) : effectiveType === DataType.IMAGE ? (
        // Guest image upload: file → immediate POST /af-image-upload →
        // pendingId in the hidden input; collectFieldValues submits the
        // {__pendingUpload} sentinel (untouched → key omitted; explicit
        // stored-image remove → null). Required-check works through
        // readInputValue's IMAGE branch (stored image satisfies required).
        <ImageUploadInput
          inputClassName={inputClassName}
          inputStyle={inputBorderStyle}
          placeholder={placeholder}
          errorColor={resolvedErrorColor}
          hasStoredImage={hasStoredImage}
          sharedAttrs={sharedAttrs}
          onChanged={handleChange}
        />
      ) : effectiveType === DataType.GUEST_STATUS ? (
        <GuestStatusInput
          initialValue={initialScalarValue}
          secondaryColor={secondaryColor}
          inputFontColor={resolvedInputFontColor}
          backgroundColor={resolvedBackgroundColor}
          borderColor={resolvedBorderColor}
          roundedClasses={roundedClasses}
          resolvedRounded={resolvedRounded}
          effectiveBorder={effectiveBorder}
          acceptButtonText={acceptButtonText ?? t("yes")}
          declineButtonText={declineButtonText ?? t("no")}
          selectedButtonColor={selectedButtonColor}
          selectedButtonBackground={selectedButtonBackground}
          hoverButtonColor={hoverButtonColor}
          hoverButtonBackground={hoverButtonBackground}
          errorColor={resolvedErrorColor}
          sharedAttrs={sharedAttrs}
        />
      ) : effectiveType === DataType.SINGLE_SELECT ? (
        <select
          className={inputClassName}
          defaultValue={initialScalarValue}
          style={inputBorderStyle}
          onChange={handleChange}
          {...sharedAttrs}
        >
          <option value="">{placeholder}</option>
          {selectOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : effectiveType === DataType.MULTI_SELECT ? (
        <select
          className={inputClassName}
          defaultValue={initialMultiValue}
          style={inputBorderStyle}
          onChange={handleChange}
          multiple
          {...sharedAttrs}
        >
          {selectOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : multiline ? (
        <textarea
          className={inputClassName}
          rows={multilineRows}
          placeholder={placeholder}
          defaultValue={initialScalarValue}
          style={inputBorderStyle}
          onChange={handleChange}
          {...sharedAttrs}
        />
      ) : (
        <input
          type="text"
          className={inputClassName}
          placeholder={placeholder}
          defaultValue={initialScalarValue}
          style={inputBorderStyle}
          onChange={handleChange}
          {...sharedAttrs}
        />
      )}

      {error ? (
        <p
          className="mt-1"
          style={{
            fontSize: "0.75rem",
            color: resolvedErrorColor,
          }}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

export {
  DataInputAcceptanceField,
  GuestStatusInput,
  resolveDataInputSubmittedFlag,
  resolveSessionPrefillValue,
  resolveCompanionSessionPrefillValue,
  resolveDraftPrefillValue,
  normalizeInputDefaultValue,
  parseBooleanValue,
  fieldTypeFromProps,
  buildInputCommonStyle,
};
